import React, { useEffect, useMemo, useState } from "react"
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    Paper,
    Snackbar,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import EditIcon from "@mui/icons-material/Edit"
import SaveIcon from "@mui/icons-material/Save"
import CloseIcon from "@mui/icons-material/Close"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import apiClient from "../../services/apiClient"
import * as adminService from "../../services/adminService"
import PricingGridTable, {
    PricingGridDraft,
    PricingGridRow,
} from "../../components/pricing/PricingGridTable"
import SimpleSalesStats from "../../components/stats/SimpleSalesStats"
import { listCustomerTypes } from "../../services/customerTypesService"
import {
    CategoryDetail,
    CategoryStatsPayload,
    getCategory,
    getCategoryStats,
    updateCategory,
} from "../../services/categoriesService"
import {
    CategoryPricingRule,
    createCategoryPricingRule,
    deleteCategoryPricingRule,
    listCategoryPricingRules,
    updateCategoryPricingRule,
} from "../../services/pricingService"

type CategoryFormState = {
    name: string
    description: string
    unitPrice: string
    isArchived: boolean
}

const CURRENCY_INPUT_REGEX = /^\d*(?:[.,]\d{0,2})?$/
const CURRENCY_STRICT_REGEX = /^\d+(?:[.,]\d{1,2})?$/
const UUID_REGEX =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function formatMinor(minor?: number | null) {
    const value = Number(minor ?? 0)
    return `${(value / 100).toFixed(2)} €`
}

function toFormState(category: CategoryDetail): CategoryFormState {
    return {
        name: category.name || "",
        description: category.description || "",
        unitPrice:
            category.unit_price_minor === null ||
            category.unit_price_minor === undefined
                ? ""
                : (Number(category.unit_price_minor) / 100).toFixed(2),
        isArchived: !!category.is_archived,
    }
}

function CategoryDetailPage() {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const { categoryId } = useParams<{ categoryId: string }>()

    const [companyId, setCompanyId] = useState("")
    const [category, setCategory] = useState<CategoryDetail | null>(null)
    const [form, setForm] = useState<CategoryFormState>({
        name: "",
        description: "",
        unitPrice: "",
        isArchived: false,
    })
    const [isEditing, setIsEditing] = useState(false)
    const [loading, setLoading] = useState(true)
    const [notFound, setNotFound] = useState(false)
    const [loadError, setLoadError] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)
    const [statsLoading, setStatsLoading] = useState(false)
    const [stats, setStats] = useState<CategoryStatsPayload | null>(null)
    const [pricingRules, setPricingRules] = useState<CategoryPricingRule[]>([])
    const [pricingLoading, setPricingLoading] = useState(false)
    const [customerTypeOptions, setCustomerTypeOptions] = useState<
        { id: string; label: string }[]
    >([])
    const [defaultPriceGridId, setDefaultPriceGridId] = useState("")
    const [snackbar, setSnackbar] = useState<{
        open: boolean
        message: string
        severity: "success" | "error"
    }>({
        open: false,
        message: "",
        severity: "success",
    })

    useEffect(() => {
        let cancelled = false

        ;(async () => {
            try {
                const res = await apiClient.get("/api/companies")
                if (cancelled) return
                const companies = res.companies || []
                if (companies.length > 0) {
                    setCompanyId(companies[0].id)
                }
            } catch (error) {
                console.error("Failed to load companies:", error)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [])

    useEffect(() => {
        if (!companyId) return

        if (!categoryId || !UUID_REGEX.test(categoryId)) {
            setCategory(null)
            setNotFound(true)
            setLoadError(null)
            setLoading(false)
            return
        }

        let cancelled = false

        ;(async () => {
            setLoading(true)
            setNotFound(false)
            setLoadError(null)
            try {
                const loadedCategory = await getCategory(
                    companyId,
                    categoryId,
                    {
                        includeArchived: true,
                    }
                )
                if (cancelled) return

                setCategory(loadedCategory)
                setForm(toFormState(loadedCategory))
            } catch (error) {
                console.error("Failed to load category:", error)
                if (!cancelled) {
                    const err: any = error
                    setCategory(null)
                    setNotFound(err?.statusCode === 404)
                    if (err?.statusCode !== 404) {
                        setLoadError(
                            err?.message ||
                                String(
                                    t("categoryDetailPage.errors.loadFailed")
                                )
                        )
                    }
                }
            } finally {
                if (!cancelled) {
                    setLoading(false)
                    setIsEditing(false)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [companyId, categoryId])

    useEffect(() => {
        if (!companyId) return

        let cancelled = false

        ;(async () => {
            try {
                const [customerTypesResult, gridsResult] = await Promise.allSettled(
                    [
                        listCustomerTypes(companyId),
                        adminService.getPriceGrids(companyId, {
                            activeOnly: true,
                        }),
                    ]
                )

                if (cancelled) return

                if (customerTypesResult.status === "fulfilled") {
                    const mapped = (customerTypesResult.value || []).map(
                        (ct: any) => ({
                            id: ct.id,
                            label: ct.name || ct.id,
                        })
                    )
                    setCustomerTypeOptions(mapped)
                }

                if (gridsResult.status === "fulfilled") {
                    const grids = gridsResult.value || []
                    const preferredGrid =
                        grids.find(
                            (grid: any) =>
                                grid.scope === "company" && grid.is_active
                        ) ||
                        grids.find((grid: any) => grid.is_active) ||
                        grids[0] ||
                        null
                    setDefaultPriceGridId(preferredGrid?.id || "")
                }
            } catch (error) {
                console.error("Failed to load pricing support data:", error)
            }
        })()

        return () => {
            cancelled = true
        }
    }, [companyId])

    useEffect(() => {
        if (!companyId || !categoryId) return

        let cancelled = false

        ;(async () => {
            setPricingLoading(true)
            try {
                const rules = await listCategoryPricingRules(
                    companyId,
                    categoryId
                )
                if (cancelled) return
                setPricingRules(rules || [])
            } catch (error) {
                console.error("Failed to load category pricing rules:", error)
                if (!cancelled) {
                    setPricingRules([])
                }
            } finally {
                if (!cancelled) {
                    setPricingLoading(false)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [companyId, categoryId])

    useEffect(() => {
        if (!companyId || !categoryId) return

        let cancelled = false

        ;(async () => {
            setStatsLoading(true)
            try {
                const loadedStats = await getCategoryStats(
                    companyId,
                    categoryId
                )
                if (cancelled) return
                setStats(loadedStats)
            } catch (error) {
                console.error("Failed to load category stats:", error)
                if (!cancelled) {
                    setStats({
                        revenue_total_minor: 0,
                        monthly_sales: [],
                    })
                }
            } finally {
                if (!cancelled) {
                    setStatsLoading(false)
                }
            }
        })()

        return () => {
            cancelled = true
        }
    }, [companyId, categoryId])

    const canSave = useMemo(() => {
        return form.name.trim().length > 0 && !saving
    }, [form.name, saving])

    const isUnitPriceValid = useMemo(() => {
        const value = form.unitPrice.trim()
        if (!value) return true
        return CURRENCY_STRICT_REGEX.test(value)
    }, [form.unitPrice])

    const pricingRows = useMemo<PricingGridRow[]>(() => {
        return pricingRules.map((rule) => {
            const customerTypeLabel =
                customerTypeOptions.find(
                    (ct) => ct.id === rule.customer_type_id
                )?.label ||
                (rule.customer_type_id
                    ? rule.customer_type_id
                    : String(t("categoryDetailPage.pricing.allCustomerTypes")))

            return {
                id: rule.id,
                customerTypeId: rule.customer_type_id || null,
                customerTypeLabel,
                unitPriceMinor: Number(rule.unit_price_minor || 0),
                effectiveFrom: rule.effective_from || null,
                effectiveTo: rule.effective_to || null,
            }
        })
    }, [pricingRules, customerTypeOptions, t])

    const handleStartEdit = () => {
        if (!category) return
        setForm(toFormState(category))
        setIsEditing(true)
    }

    const handleCancel = () => {
        if (!category) return
        setForm(toFormState(category))
        setIsEditing(false)
    }

    const handleSave = async () => {
        if (!categoryId || !companyId) return

        const trimmedName = form.name.trim()
        if (!trimmedName) {
            setSnackbar({
                open: true,
                message: String(
                    t("categoryDetailPage.validation.nameRequired")
                ),
                severity: "error",
            })
            return
        }

        let unitPriceMinor: number | null = null
        const priceInput = form.unitPrice.trim()
        if (priceInput) {
            if (!CURRENCY_STRICT_REGEX.test(priceInput)) {
                setSnackbar({
                    open: true,
                    message: String(
                        t("categoryDetailPage.validation.invalidPriceFormat")
                    ),
                    severity: "error",
                })
                return
            }

            const normalized = priceInput.replace(",", ".")
            const parsed = Number(normalized)
            if (!Number.isFinite(parsed) || parsed < 0) {
                setSnackbar({
                    open: true,
                    message: String(
                        t("categoryDetailPage.validation.invalidPositivePrice")
                    ),
                    severity: "error",
                })
                return
            }
            unitPriceMinor = Math.round(parsed * 100)
        }

        setSaving(true)
        try {
            const updated = await updateCategory(companyId, categoryId, {
                name: trimmedName,
                description: form.description.trim() || null,
                unit_price_minor: unitPriceMinor,
                is_archived: form.isArchived,
            })

            setCategory(updated)
            setForm(toFormState(updated))
            setIsEditing(false)
            setSnackbar({
                open: true,
                message: String(t("categoryDetailPage.messages.updated")),
                severity: "success",
            })
        } catch (error: any) {
            const message =
                error?.message ||
                String(t("categoryDetailPage.errors.saveFailed"))
            setSnackbar({ open: true, message, severity: "error" })
        } finally {
            setSaving(false)
        }
    }

    const handleCreatePricingRule = async (draft: PricingGridDraft) => {
        if (!companyId || !categoryId) return

        const priceGridId =
            defaultPriceGridId || pricingRules[0]?.price_grid_id || ""
        if (!priceGridId) {
            throw new Error(
                String(t("categoryDetailPage.pricing.errors.noActiveGrid"))
            )
        }

        try {
            await createCategoryPricingRule(companyId, categoryId, {
                ...draft,
                price_grid_id: priceGridId,
                price_type: "base",
                currency: "EUR",
            })

            const refreshed = await listCategoryPricingRules(
                companyId,
                categoryId
            )
            setPricingRules(refreshed || [])
            setSnackbar({
                open: true,
                message: String(
                    t("categoryDetailPage.pricing.messages.ruleAdded")
                ),
                severity: "success",
            })
        } catch (error: any) {
            const message =
                error?.message ||
                String(t("categoryDetailPage.pricing.errors.createRuleFailed"))
            setSnackbar({ open: true, message, severity: "error" })
            throw error
        }
    }

    const handleUpdatePricingRule = async (
        ruleId: string,
        draft: PricingGridDraft
    ) => {
        if (!companyId || !categoryId) return

        try {
            const existing = pricingRules.find((rule) => rule.id === ruleId)
            const priceGridId = existing?.price_grid_id || defaultPriceGridId

            await updateCategoryPricingRule(companyId, categoryId, ruleId, {
                ...draft,
                ...(priceGridId ? { price_grid_id: priceGridId } : {}),
            })

            const refreshed = await listCategoryPricingRules(
                companyId,
                categoryId
            )
            setPricingRules(refreshed || [])
            setSnackbar({
                open: true,
                message: String(
                    t("categoryDetailPage.pricing.messages.ruleUpdated")
                ),
                severity: "success",
            })
        } catch (error: any) {
            const message =
                error?.message ||
                String(t("categoryDetailPage.pricing.errors.updateRuleFailed"))
            setSnackbar({ open: true, message, severity: "error" })
            throw error
        }
    }

    const handleDeletePricingRule = async (ruleId: string) => {
        if (!companyId || !categoryId) return

        try {
            await deleteCategoryPricingRule(companyId, categoryId, ruleId)
            const refreshed = await listCategoryPricingRules(
                companyId,
                categoryId
            )
            setPricingRules(refreshed || [])
            setSnackbar({
                open: true,
                message: String(
                    t("categoryDetailPage.pricing.messages.ruleDeleted")
                ),
                severity: "success",
            })
        } catch (error: any) {
            const message =
                error?.message ||
                String(t("categoryDetailPage.pricing.errors.deleteRuleFailed"))
            setSnackbar({ open: true, message, severity: "error" })
            throw error
        }
    }

    if (loading) {
        return (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        )
    }

    if (!category) {
        return (
            <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        {notFound
                            ? t("categoryDetailPage.notFound.title")
                            : t("categoryDetailPage.errors.loadTitle")}
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 2 }}>
                        {notFound
                            ? t("categoryDetailPage.notFound.message")
                            : loadError ||
                              t("categoryDetailPage.errors.unexpected")}
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/admin/categories")}
                    >
                        {t("categoryDetailPage.actions.backToCategories")}
                    </Button>
                </Paper>
            </Box>
        )
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1000, mx: "auto" }}>
            <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "stretch", sm: "center" }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 3 }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <Button
                        variant="text"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/admin/categories")}
                    >
                        {t("categoryDetailPage.actions.back")}
                    </Button>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {category.name}
                    </Typography>
                    <Chip
                        label={
                            category.is_archived
                                ? t("categoryDetailPage.status.archived")
                                : t("categoryDetailPage.status.active")
                        }
                        size="small"
                        color={category.is_archived ? "default" : "success"}
                        variant={category.is_archived ? "outlined" : "filled"}
                    />
                </Stack>

                {!isEditing ? (
                    <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={handleStartEdit}
                    >
                        {t("categoryDetailPage.actions.edit")}
                    </Button>
                ) : (
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            color="inherit"
                            startIcon={<CloseIcon />}
                            onClick={handleCancel}
                            disabled={saving}
                        >
                            {t("common.cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={!canSave}
                        >
                            {saving ? t("common.saving") : t("common.save")}
                        </Button>
                    </Stack>
                )}
            </Stack>

            <Paper sx={{ p: 3, border: "1px solid #e5e7eb" }}>
                <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label={String(t("categoryDetailPage.fields.name"))}
                            value={form.name}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                }))
                            }
                            fullWidth
                            disabled={!isEditing || saving}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            label={String(
                                t("categoryDetailPage.fields.baseSalePrice")
                            )}
                            value={form.unitPrice}
                            onChange={(e) => {
                                const nextValue = e.target.value
                                if (!CURRENCY_INPUT_REGEX.test(nextValue))
                                    return
                                setForm((prev) => ({
                                    ...prev,
                                    unitPrice: nextValue,
                                }))
                            }}
                            onBlur={() => {
                                const value = form.unitPrice.trim()
                                if (
                                    !value ||
                                    !CURRENCY_STRICT_REGEX.test(value)
                                )
                                    return
                                const normalized = Number(
                                    value.replace(",", ".")
                                )
                                setForm((prev) => ({
                                    ...prev,
                                    unitPrice: normalized.toString(),
                                }))
                            }}
                            fullWidth
                            disabled={!isEditing || saving}
                            placeholder={String(
                                t(
                                    "categoryDetailPage.fields.baseSalePricePlaceholder"
                                )
                            )}
                            error={isEditing && !isUnitPriceValid}
                            helperText={
                                isEditing && !isUnitPriceValid
                                    ? t(
                                          "categoryDetailPage.validation.priceFormatHint"
                                      )
                                    : ""
                            }
                            inputProps={{
                                inputMode: "decimal",
                                pattern: "^\\d*(?:[.,]\\d{0,2})?$",
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label={String(
                                t("categoryDetailPage.fields.description")
                            )}
                            value={form.description}
                            onChange={(e) =>
                                setForm((prev) => ({
                                    ...prev,
                                    description: e.target.value,
                                }))
                            }
                            fullWidth
                            multiline
                            minRows={3}
                            disabled={!isEditing || saving}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Switch
                                checked={!form.isArchived}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        isArchived: !e.target.checked,
                                    }))
                                }
                                disabled={!isEditing || saving}
                            />
                            <Typography>
                                {form.isArchived
                                    ? t(
                                          "categoryDetailPage.statusLabel.archived"
                                      )
                                    : t(
                                          "categoryDetailPage.statusLabel.active"
                                      )}
                            </Typography>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ mt: 2 }}>
                <SimpleSalesStats
                    title={String(t("categoryDetailPage.stats.title"))}
                    loading={statsLoading}
                    revenueTotalMinor={stats?.revenue_total_minor || 0}
                    monthly={(stats?.monthly_sales || []).map((row) => ({
                        month: row.month,
                        revenueMinor: Number(row.revenue_minor || 0),
                    }))}
                    emptyMessage={String(t("categoryDetailPage.stats.empty"))}
                />
            </Box>

            <Box sx={{ mt: 2 }}>
                <PricingGridTable
                    title={String(t("categoryDetailPage.pricing.title"))}
                    rows={pricingRows}
                    customerTypes={customerTypeOptions}
                    loading={pricingLoading}
                    onCreate={handleCreatePricingRule}
                    onUpdate={handleUpdatePricingRule}
                    onDelete={handleDeletePricingRule}
                />
            </Box>

            <Paper sx={{ p: 3, border: "1px solid #e5e7eb", mt: 2 }}>
                <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                >
                    {t("categoryDetailPage.readOnly.title")}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {t("categoryDetailPage.readOnly.items")}
                        </Typography>
                        <Typography color="text.secondary">
                            {Number(category.items_count || 0)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {t("categoryDetailPage.readOnly.currentPrice")}
                        </Typography>
                        <Typography color="text.secondary">
                            {formatMinor(category.unit_price_minor)}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography sx={{ fontWeight: 600 }}>
                            {t("categoryDetailPage.readOnly.lastUpdate")}
                        </Typography>
                        <Typography color="text.secondary">
                            {category.updated_at
                                ? new Date(category.updated_at).toLocaleString(
                                      i18n.language || "fr"
                                  )
                                : t("common.dash")}
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={() =>
                    setSnackbar((prev) => ({ ...prev, open: false }))
                }
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                    onClose={() =>
                        setSnackbar((prev) => ({ ...prev, open: false }))
                    }
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default CategoryDetailPage
